import { useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SectionCard, Field } from '../pipeline/ui';
import { formatCreditsOnly } from '../../lib/credits';

const ERR_KEY = {
  promo_not_found: 'redeem.errNotFound',
  promo_expired: 'redeem.errExpired',
  promo_exhausted: 'redeem.errExhausted',
  promo_already_redeemed: 'redeem.errAlready',
};

// User-facing "redeem a promo code" card. Calls the api-router redeem_promo action; success shows
// credits added + new balance via the shared formatter; each server error maps to an inline message.
export default function RedeemCard({ peg = 0.01 }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!code.trim()) return;
    setError(''); setResult(null); setBusy(true);
    try {
      const r = await api.out('redeem_promo', { code: code.trim() });
      setResult(r); setCode('');
      toast.success(t('redeem.success'));
    } catch (e) {
      setError(t(ERR_KEY[e?.message] || 'redeem.errGeneric'));
    } finally { setBusy(false); }
  };

  return (
    <SectionCard title={t('redeem.title')} testId="redeem-card">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={<T k="redeem.codeLabel" />}>
          <Input
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(''); }}
            placeholder={t('redeem.codePlaceholder')}
            data-testid="redeem-code"
            className="w-64"
          />
        </Field>
        <Button onClick={submit} disabled={busy || !code.trim()} data-testid="redeem-submit"><T k="redeem.submit" /></Button>
      </div>
      {error && <p className="mt-3 text-sm text-destructive" data-testid="redeem-error">{error}</p>}
      {result && (
        <p className="mt-3 text-sm text-success" data-testid="redeem-success">
          {t('redeem.success')} · +{Number(result.credits).toLocaleString()} · {t('wallet.credits', { balance: formatCreditsOnly(result.wallet?.balance_credits) })}
        </p>
      )}
    </SectionCard>
  );
}
